import type { Translation } from '../../types';
import type { reporting as en } from '../en/reporting';

/** Cài đặt › Kết nối › API báo cáo (docs/specs/reporting-api.md §12, §13). Lời thường, không dùng từ kỹ thuật. */
export const reporting: Translation<typeof en> = {
  title: 'API báo cáo',
  intro:
    'Cho phép hệ thống quản lý khách hàng (như Twenty) và công cụ báo cáo nội bộ đọc dữ liệu kinh doanh. Chỉ đọc. Không bao giờ gửi giấy tờ tuỳ thân, ngày sinh, mã số thuế cá nhân, dữ liệu định danh điện tử hay hồ sơ tên miền.',
  loadFailed: 'Không tải được danh sách nơi nhận dữ liệu',
  production: 'Đây là máy chủ chính thức: mọi khoá đều phải giới hạn địa chỉ được dùng.',
  yes: 'Có',
  no: 'Không',
  close: 'Đóng',
  copy: 'Sao chép',
  copied: 'Đã sao chép',
  more: 'Thêm',
  scope: {
    customers: { label: 'Khách hàng', hint: 'Tài khoản khách hàng và loại khách.' },
    domains: { label: 'Tên miền', hint: 'Tên miền, trạng thái, ngày hết hạn và tình trạng hồ sơ.' },
    orders: { label: 'Đơn hàng', hint: 'Đơn hàng và từng mục trong đơn, kèm tiền trước thuế, thuế và phí nộp hộ VNNIC.' },
    invoices: { label: 'Hoá đơn', hint: 'Chứng từ nội bộ và chứng từ điều chỉnh giảm, từng dòng.' },
    payments: { label: 'Thanh toán', hint: 'Các lần nạp tiền và thanh toán.' },
    refunds: { label: 'Hoàn tiền', hint: 'Các khoản hoàn tiền.' },
    balances: { label: 'Số dư ví', hint: 'Số dư ví của khách và tuổi số dư.' },
    stats: { label: 'Số liệu theo ngày', hint: 'Đăng ký, gia hạn, doanh thu theo ngày, đuôi tên miền và kênh.' },
  },
  dataset: {
    customers: 'Khách hàng',
    domains: 'Tên miền',
    orders: 'Đơn hàng',
    order_items: 'Mục đơn hàng',
    invoices: 'Hoá đơn',
    invoice_lines: 'Dòng hoá đơn',
    payments: 'Thanh toán',
    refunds: 'Hoàn tiền',
    balances: 'Số dư ví',
    daily_stats: 'Số liệu theo ngày',
  },
  places: {
    title: 'Nơi nhận dữ liệu',
    description: 'Mỗi nơi nhận có khoá riêng và tự giữ vị trí đã nhận đến: đổi khoá không làm nơi nhận lấy lại từ đầu.',
    add: 'Thêm nơi nhận',
    empty: 'Chưa có nơi nào nhận dữ liệu',
    emptyHint: 'Thêm một nơi nhận (ví dụ "Twenty CRM"), chọn dữ liệu được nhận rồi tạo khoá.',
  },
  place: {
    receives: 'Dữ liệu được nhận',
    nothing: 'Chưa nhận gì',
    contacts: 'Email, điện thoại khách',
    weekly: 'Gửi lại toàn bộ mỗi tuần',
    addedBy: 'Thêm bởi {{name}} · {{when}}',
    added: 'Thêm {{when}}',
    state: { RECEIVING: 'Đang nhận', NOTHING: 'Chưa nhận dữ liệu', NO_KEY: 'Chưa có khoá', DISABLED: 'Đã tắt' },
    syncTitle: 'Cập nhật đến',
    syncNone: 'Bắt đầu từ lần lấy đầu tiên.',
    sync: {
      never: '{{label}}: chưa lấy lần nào',
      catchingUp: '{{label}}: còn khoảng {{rows}} dòng, xong sau khoảng {{minutes}} phút',
      catchingUpNoEta: '{{label}}: còn khoảng {{rows}} dòng',
      lagging: '{{label}}: chậm {{minutes}} phút',
      upToDate: '{{label}}: đã cập nhật',
    },
  },
  warning: {
    KEY_EXPIRING: 'Khoá sắp hết hạn',
    KEY_WITHOUT_ADDRESSES: 'Khoá chưa giới hạn địa chỉ',
    KEY_SUSPENDED: 'Khoá đang bị tạm khoá',
    NOT_FETCHED_2H: '{{name}} chưa lấy {{label}} hơn 2 giờ',
    LAGGING_1H: '{{name}} đang chậm hơn 1 giờ ở {{label}}',
    PARKED_ROWS: 'Có {{count}} dòng {{label}} {{name}} chưa nhận được',
  },
  keys: {
    title: 'Khoá',
    empty: 'Chưa có khoá. Tạo một khoá cho mỗi cách dùng (lấy theo lịch, lấy khi được gọi).',
    col: { key: 'Khoá', purpose: 'Dùng cho', createdBy: 'Người tạo', lastUsed: 'Dùng lần cuối', expires: 'Hết hạn', state: 'Trạng thái' },
    neverUsed: 'Chưa dùng',
    from: 'từ {{ip}}',
    anyAddress: 'Mọi địa chỉ',
    addresses_one: '{{count}} địa chỉ',
    addresses_other: '{{count}} địa chỉ',
    expiresOn: '{{date}}',
    revokedOn: 'Thu hồi {{when}}',
    replaces: 'Thay cho một khoá trước',
  },
  purpose: { SCHEDULED: 'Lấy dữ liệu theo lịch', TRIGGERED: 'Lấy dữ liệu khi được gọi' },
  keyState: {
    ACTIVE: 'Đang dùng',
    EXPIRING: 'Sắp hết hạn',
    SUSPENDED: 'Đang bị tạm khoá',
    EXPOSED: 'Bị lộ, đang thu hồi',
    REVOKED: 'Đã thu hồi',
    EXPIRED: 'Đã hết hạn',
  },
  suspended: {
    BUDGET_EXCEEDED: 'Lấy vượt hạn mức trong ngày',
    NETWORK_NOT_ALLOWED: 'Khoá được dùng từ địa chỉ không được phép',
    EXPOSED_IN_URL: 'Khoá bị gửi kèm trong địa chỉ trang',
    OTHER: 'Hệ thống tạm khoá',
  },
  add: {
    title: 'Thêm nơi nhận dữ liệu',
    description: 'Nơi nhận mới chưa nhận dữ liệu nào và không nhận email, điện thoại khách cho đến khi anh/chị chọn.',
    name: 'Tên nơi nhận',
    namePlaceholder: 'Twenty CRM',
    note: 'Ghi chú',
    submit: 'Thêm',
    done: 'Đã thêm. Hãy chọn dữ liệu được nhận và tạo khoá.',
    exists: 'Đã có nơi nhận trùng tên.',
    stepReceive: '1. Dữ liệu được nhận',
    stepKey: '2. Khoá',
    nextKey: 'Tiếp: tạo khoá',
    later: 'Để sau',
    keyAfterContacts: 'Tuỳ chọn email, điện thoại khách đang chờ người khác duyệt. Hãy tạo khoá sau khi được duyệt.',
  },
  receive: {
    action: 'Sửa dữ liệu được nhận',
    title: 'Dữ liệu {{name}} được nhận',
    description: 'Thay đổi được gửi duyệt trước khi áp dụng.',
    kinds: 'Loại dữ liệu',
    contacts: 'Kèm email, số điện thoại và tên khách cá nhân',
    contactsHelp:
      'Đây là dữ liệu cá nhân. Chỉ bật khi cần liên hệ khách từ {{name}}. Áp dụng cho mọi khách hàng, trừ khách đã yêu cầu không gửi. Cần một người khác có quyền xử lý dữ liệu cá nhân duyệt.',
    confirmIntro: 'Xác nhận từng điểm trước khi gửi:',
    confirm: {
      hosting: '{{name}} đặt tại Việt Nam, trên máy chủ do Tino quản lý.',
      run_history: 'Lịch sử chạy quy trình ở {{name}} chỉ được giữ tối đa 7 ngày.',
      viewers: 'Chỉ quản trị viên của {{name}} xem được lịch sử chạy quy trình.',
    },
    contactsOff:
      'Tắt thì email, số điện thoại và tên khách cá nhân đã gửi sẽ được xoá khỏi các bản ghi ở {{name}} ở lần lấy tiếp theo. Lịch sử chạy quy trình ở {{name}} cần xoá riêng.',
    removed: '{{name}} sẽ không nhận thêm {{list}}. Dữ liệu đã gửi vẫn còn ở {{name}}; hãy xoá ở đó nếu không cần.',
    missing: '{{label}} sẽ ghi mã {{parents}} dạng chữ vì {{name}} không nhận {{parents}}.',
    weekly: 'Gửi lại toàn bộ mỗi tuần để sửa sai lệch',
    weeklyHint: 'Sửa các bản ghi bị sửa hoặc xoá tay ở {{name}}.',
    noChange: 'Hãy thay đổi ít nhất một mục.',
    submit: 'Gửi duyệt',
  },
  key: {
    create: 'Tạo khoá',
    title: 'Tạo khoá cho {{name}}',
    description: 'Khoá được tạo khi yêu cầu được duyệt và chỉ hiện một lần.',
    name: 'Tên khoá',
    namePlaceholder: 'Twenty – máy chủ chính',
    purpose: 'Dùng cho',
    purposeHint: {
      SCHEDULED: 'Quy trình chạy 15 phút một lần.',
      TRIGGERED: 'Quy trình lấy tiếp ngay khi còn dữ liệu.',
    },
    addresses: 'Chỉ nhận từ địa chỉ',
    addressesPlaceholder: '203.0.113.10\n203.0.113.0/24',
    addressesHelp: 'Mỗi dòng một địa chỉ hoặc một dải địa chỉ.',
    addressesRequired: 'Địa chỉ của máy chủ {{name}}. Bắt buộc.',
    addressesOptional: 'Để trống thì mọi địa chỉ đều dùng được khoá. Không nên để trống.',
    addressesInvalid: 'Không phải địa chỉ hay dải địa chỉ: {{list}}',
    addressesTooMany: 'Tối đa {{max}} địa chỉ.',
    expires: 'Hết hạn sau',
    expiry: { '90d': '90 ngày', '6m': '6 tháng', '1y': '1 năm', '2y': '2 năm' },
    contactsNote: 'Nơi nhận này đang nhận email, số điện thoại khách: cần một người khác có quyền xử lý dữ liệu cá nhân duyệt.',
    submitted: 'Đã gửi duyệt. Khi được duyệt, bấm «Lấy khoá» để xem khoá một lần.',
  },
  rotate: {
    action: 'Đổi khoá',
    title: 'Đổi khoá {{key}}',
    description: 'Khoá mới dùng cho cùng việc. Khoá cũ vẫn dùng được thêm một thời gian rồi ngừng.',
    overlap: 'Khoá cũ vẫn dùng được thêm',
    overlapHint: 'để kịp thay ở {{name}}.',
    overlapDays: { '1': '1 ngày', '7': '7 ngày', '30': '30 ngày' },
  },
  revoke: {
    action: 'Thu hồi',
    title: 'Thu hồi khoá {{key}}',
    warning: '{{name}} sẽ không lấy được dữ liệu bằng khoá này nữa, ngay lập tức.',
    done: 'Đã thu hồi.',
  },
  resume: {
    action: 'Mở lại khoá',
    title: 'Mở lại khoá {{key}}',
    why: 'Lý do bị tạm khoá: {{reason}}.',
    warning: 'Chỉ mở lại khi đã chắc khoá không bị lộ. Nếu nghi bị lộ, hãy thu hồi và tạo khoá mới.',
    submit: 'Mở lại',
    done: 'Khoá đã dùng lại được.',
    exposed: 'Khoá này đã bị gửi kèm trong địa chỉ trang. Hãy thu hồi và tạo khoá mới.',
  },
  secret: {
    title: 'Khoá truy cập mới',
    warning:
      'Sao chép khoá và dán vào {{name}} ngay. Khoá chỉ hiện một lần; đóng cửa sổ này thì không ai xem lại được. Nếu làm mất, hãy đổi khoá.',
    key: 'Khoá',
    address: 'Địa chỉ lấy dữ liệu',
    takeKey: 'Lấy khoá',
  },
  approval: {
    contactsTitle: 'Trước khi duyệt: email, điện thoại khách ở {{name}}',
    contactsHint: 'Anh/chị có quyền xử lý dữ liệu cá nhân. Hãy xác nhận từng điểm; câu trả lời được lưu cùng lần duyệt.',
    waitSecond: 'Cần một người khác quản lý API báo cáo duyệt. Người đó sẽ thấy yêu cầu ở trang này hoặc ở mục Phê duyệt.',
    waitPersonalData: 'Việc này gửi email, số điện thoại khách: cần một người khác có quyền xử lý dữ liệu cá nhân duyệt. Người đó sẽ thấy yêu cầu ở trang này hoặc ở mục Phê duyệt.',
    pendingHint: 'Thay đổi dữ liệu được nhận và khoá mới đều cần người thứ hai duyệt. Khoá mới chỉ hiện một lần, khi bấm «Lấy khoá».',
  },
  history: {
    action: 'Lịch sử',
    title: 'Lịch sử của {{name}}',
    calls24h: 'Lần lấy trong 24 giờ',
    rowsToday: 'Dòng đã gửi hôm nay',
    budget: 'Hạn mức trong ngày {{budget}}',
    errors24h: 'Lỗi trong 24 giờ',
    onlyErrors: 'Chỉ xem lỗi',
    col: { at: 'Thời gian', data: 'Dữ liệu', rows: 'Số dòng', contacts: 'Kèm email, điện thoại', result: 'Kết quả', from: 'Từ địa chỉ', key: 'Khoá' },
    empty: 'Không có lần lấy nào trong 30 ngày qua.',
    noErrors: 'Không có lỗi nào trong 30 ngày qua.',
    events: 'Sự kiện',
    event: {
      'reporting_key.suspend': 'Khoá bị tạm khoá: {{reason}}',
      'reporting_key.resume': 'Khoá được mở lại',
      'reporting_checkpoint.reset': 'Gửi lại toàn bộ',
      'reporting_key.rotate': 'Đổi khoá',
      'reporting_key.revoke': 'Thu hồi khoá',
      'reporting_consumer.change': 'Đổi dữ liệu được nhận',
      other: 'Thay đổi',
    },
    other: 'Khác',
    page: 'Trang {{page}}/{{pages}}',
    note: '30 ngày gần nhất. Không bao giờ ghi lại nội dung dữ liệu.',
  },
  result: {
    OK: 'Thành công',
    WRONG_SECRET: 'Sai khoá',
    REVOKED: 'Khoá đã thu hồi',
    EXPIRED: 'Khoá đã hết hạn',
    SUSPENDED: 'Khoá đang bị tạm khoá',
    NETWORK_NOT_ALLOWED: 'Địa chỉ không được phép',
    KEY_WITHOUT_ADDRESSES: 'Khoá chưa giới hạn địa chỉ được dùng',
    DATASET_NOT_ALLOWED: 'Nơi nhận không nhận loại dữ liệu này',
    RATE_LIMITED: 'Gọi quá nhiều, đã tạm chặn',
    UNAVAILABLE: 'Tạm thời không phục vụ được',
    KEY_IN_ADDRESS: 'Khoá bị gửi kèm trong địa chỉ trang',
    CONSUMER_DISABLED: 'Nơi nhận đã bị tắt',
    INVALID: 'Yêu cầu không hợp lệ',
    OTHER: 'Không thành công',
  },
  reset: {
    action: 'Đồng bộ lại từ đầu',
    title: 'Đồng bộ lại từ đầu cho {{name}}',
    pick: 'Loại dữ liệu',
    warning:
      'Từ lần lấy tiếp theo, toàn bộ dữ liệu đã chọn sẽ được gửi lại. {{name}} cập nhật lại, không tạo bản trùng. Việc này có thể mất một lúc; xem tiến độ ở cột «Cập nhật đến».',
    contactsWarning: 'Việc này gửi lại email, số điện thoại khách: cần một người khác có quyền xử lý dữ liệu cá nhân duyệt.',
    submit: 'Đồng bộ lại',
    done: 'Gửi lại từ lần lấy tiếp theo: {{list}}.',
    proposed: 'Đã gửi duyệt: {{list}}.',
  },
  parked: {
    action: 'Dòng chưa nhận được',
    title: 'Dòng {{name}} chưa nhận được',
    help:
      '{{name}} đã nhiều lần không nhận được các dòng này, nên chúng được để riêng để phần còn lại vẫn cập nhật. Thường do một trường ở {{name}} thiếu lựa chọn, hoặc thiếu bản ghi liên quan. Sửa ở {{name}} rồi bấm «Gửi lại».',
    col: { data: 'Loại dữ liệu', record: 'Bản ghi', attempts: 'Số lần thử', last: 'Lần thử cuối' },
    resend: 'Gửi lại',
    resent: 'Sẽ được gửi ở lần lấy tiếp theo.',
    empty: 'Không có dòng nào bị để riêng.',
  },
  reconcile: {
    action: 'Đối chiếu số liệu',
    title: 'Số liệu đã gửi cho {{name}}',
    help:
      'So với số đếm ở {{name}}. Nếu khác, có thể bản ghi ở {{name}} đã bị sửa hoặc xoá tay; bật «Gửi lại toàn bộ mỗi tuần» hoặc bấm «Đồng bộ lại từ đầu».',
    rows: 'Số dòng',
    total: 'Tổng tiền',
    byStatus: 'Theo trạng thái',
    empty: 'Nơi nhận này chưa nhận dữ liệu nào.',
  },
  signals: {
    action: 'Báo ngay khi có thay đổi',
    title: 'Báo ngay khi có thay đổi',
    help: 'Muốn {{name}} cập nhật gần như ngay lập tức? Nhập địa chỉ nhận tín hiệu cho từng loại dữ liệu. Chỉ gửi tín hiệu «có dữ liệu mới», không kèm dữ liệu. Địa chỉ phải bắt đầu bằng https và mở được từ Internet; nếu {{name}} chỉ chạy trong mạng nội bộ, hãy để nó tự lấy dữ liệu theo lịch.',
    field: 'Địa chỉ nhận tín hiệu',
    placeholder: 'https://…',
    secret: 'Giữ bí mật địa chỉ này: ai biết địa chỉ đều có thể khởi động quy trình ở {{name}}.',
    save: 'Lưu',
    remove: 'Gỡ',
    change: 'Đổi',
    saved: 'Đã lưu.',
    removed: 'Đã gỡ.',
    none: 'Chưa đặt',
    failing: 'Lỗi {{count}} lần liên tiếp',
    empty: 'Nơi nhận này chưa nhận dữ liệu nào.',
  },
  rename: {
    action: 'Đổi tên',
    title: 'Đổi tên {{name}}',
    done: 'Đã lưu.',
  },
  customer: {
    sharingTitle: 'Thông tin liên hệ gửi cho hệ thống quản lý khách hàng',
    withhold: 'Không gửi thông tin liên hệ cho hệ thống quản lý khách hàng',
    withholdHelp: 'Bật khi khách yêu cầu. Email, số điện thoại và tên của khách sẽ được xoá khỏi các nơi nhận ở lần lấy tiếp theo.',
    on: 'Đang bật',
    off: 'Đang tắt',
    withheldOn: 'Không gửi email, số điện thoại và tên của khách.',
    withheldOff: 'Thông tin liên hệ được gửi cho nơi nhận được phép nhận.',
    turnOn: 'Bật',
    turnOff: 'Tắt',
    turnOnTitle: 'Không gửi thông tin liên hệ của {{name}}',
    turnOffTitle: 'Gửi lại thông tin liên hệ của {{name}}',
    turnOffWarning: 'Chỉ tắt khi khách đồng ý. Email, số điện thoại và tên của khách sẽ được gửi lại cho nơi nhận được phép nhận ở lần lấy tiếp theo.',
    turnedOn: 'Đã bật. Thông tin liên hệ của khách sẽ được xoá khỏi các nơi nhận ở lần lấy tiếp theo.',
    turnedOff: 'Đã tắt.',
    disclosuresTitle: 'Dữ liệu của khách này đã gửi cho nơi nhận nào',
    disclosuresHelp: 'Theo nhật ký các lần lấy dữ liệu.',
    disclosuresFailed: 'Không tải được danh sách nơi nhận',
    col: { place: 'Nơi nhận', since: 'Nhận dữ liệu của khách từ', contacts: 'Email, điện thoại đã gửi' },
    notYet: 'Chưa nhận',
    never: 'Chưa bao giờ',
    contactsSent: '{{times}} lần, gần nhất {{when}}',
    noPlaces: 'Chưa có nơi nhận nào nhận dữ liệu của khách hàng.',
  },
  guide: {
    title: 'Hướng dẫn kết nối Twenty',
    description: 'Twenty tự lấy dữ liệu bằng quy trình tự động của mình; không ghi ngược lại hệ thống này. Các bước cho Twenty 2.44, không cần viết mã.',
    base: 'Địa chỉ lấy dữ liệu',
    header: 'Tên tiêu đề',
    headerValue: 'Giá trị tiêu đề',
    headerValueHint: 'Chữ Bearer, một dấu cách, rồi đến khoá.',
    download: 'Tải tài liệu kết nối',
    downloadHint: 'Mô tả từng loại dữ liệu và từng trường, có sẵn nhãn tiếng Việt để tạo trường ở Twenty.',
    downloadFailed: 'Không tải được tài liệu kết nối',
    sample: 'Tải mẫu kết quả: {{label}}',
    sampleHint: 'Dán vào phần mẫu kết quả của bước "{{step}}" ở Twenty để chọn được đúng các trường của {{label}}. Mỗi loại dữ liệu có mẫu riêng.',
    dataset: 'Ví dụ cho',
    stepsTitle: 'Hai quy trình cho mỗi loại dữ liệu',
    steps: {
      model:
        'Ở Twenty, vào Settings › Data model, tạo một đối tượng cho mỗi loại dữ liệu (ví dụ {{object}}) với các trường trong tài liệu kết nối. Chỉ khớp bản ghi theo mã (id), không bao giờ theo tên hay số.',
      schedule: 'Tạo quy trình "Tino – đồng bộ {{label}}" với kích hoạt «{{trigger}}», lịch tuỳ chỉnh {{cron}}.',
      fetch: 'Thêm bước «{{http}}» tên "{{step}}": GET địa chỉ bên dưới, tiêu đề {{header}} là khoá «Lấy dữ liệu theo lịch».',
      loop: 'Thêm bước «{{iterator}}» qua các dòng nhận được; bên trong, «{{upsert}}» vào {{object}}, khớp id với id của dòng. Gắn trường deleted vào một trường có/không "Đã xoá".',
      ack: 'Thêm bước «{{http}}» tên "Xác nhận đã nhận": POST tới địa chỉ bên dưới, cùng tiêu đề, nội dung như bên dưới.',
      more: 'Thêm «{{ifElse}}» "Còn dữ liệu?": khi còn, gọi quy trình anh em "Tino – đồng bộ {{label}} (tiếp)". Quy trình này có kích hoạt «{{onCall}}», các bước giống hệt nhưng dùng khoá «Lấy dữ liệu khi được gọi», và tự gọi lại cho đến khi hết dữ liệu.',
      never: 'Không bao giờ bật "Continue on failure" ở các bước này. Dòng nào lỗi nhiều lần sẽ được để riêng ở đây và hiện ở «Dòng chưa nhận được».',
    },
    fetchAddress: 'Lấy thay đổi',
    ackAddress: 'Xác nhận',
    ackBody: 'Nội dung xác nhận',
    rows: 'Các dòng',
    hasMore: 'Còn dữ liệu?',
    scheduleTitle: 'Lịch chạy (15 phút một lần, so le)',
    scheduleCol: { data: 'Loại dữ liệu', cron: 'Lịch', object: 'Đối tượng ở Twenty' },
    notesTitle: 'Cần lưu ý',
    notes: {
      money: 'Tiền tính bằng đồng, số nguyên: dùng trường Number. Với hoá đơn, cộng các số tiền có dấu để chứng từ điều chỉnh giảm được trừ đi.',
      months: 'Nhóm số tiền theo trường tháng và ngày gửi kèm mỗi bản ghi, không theo trường ngày giờ.',
      firstLoad: 'Những lần lấy đầu tiên gửi toàn bộ; quy trình (tiếp) chạy liên tục cho đến khi bắt kịp. Giữ 200 dòng mỗi lần lấy.',
      test: 'Để thử khoá, mở địa chỉ bên dưới với khoá trong tiêu đề: kết quả cho biết nơi nhận, dữ liệu được nhận và hạn của khoá.',
    },
    testAddress: 'Thử khoá',
  },
};
